import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useBuilding } from '../../Helpers/BuildingContext';
import { BaseComponentImpl, RoomImpl, LevelImpl } from '../../types/implementations';
import { BaseType } from '../../types/Enums';
import { BreadcrumbComponent, BreadcrumbItem } from '../../Components/BreadCrumb';
import ComponentCard from '../../Components/ComponentCard';
import ComponentDetailsView from '../ComponentPage/ComponentDetailsView';
import { translate } from '../../types/EnumTranslations';
import { Helmet } from 'react-helmet-async';

interface WindowsByParent {
    [parentKey: string]: {
        parent: BaseComponentImpl;
        windows: BaseComponentImpl[];
    };
}

interface WindowsByRoom {
    [roomKey: string]: {
        room: RoomImpl;
        level?: LevelImpl;
        windows: BaseComponentImpl[];
    };
}

const WindowsListPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const buildingKey = id || '';
    const { currentBuilding, buildingError, loadingBuilding, fetchBuilding } = useBuilding();
    const [selectedWindow, setSelectedWindow] = useState<BaseComponentImpl | null>(null);

    useEffect(() => {
        const loadBuildingData = async () => {
            if (buildingKey) {
                await fetchBuilding(buildingKey);
            }
        };

        loadBuildingData().then();
    }, [buildingKey, fetchBuilding]);

    if (loadingBuilding) return <p></p>;
    if (buildingError) return <p style={{ color: "#ff0000" }}>{buildingError}</p>;
    if (!currentBuilding) return <p>No building data available</p>;
    if (!buildingKey) return <p>No building key provided</p>;

    // Group windows by their parent components (outdoor) or by room (indoor)
    const outdoorWindowsByParent: WindowsByParent = {};
    const indoorWindowsByRoom: WindowsByRoom = {};
    let totalWindowCount = 0;

    if (currentBuilding.model?.structure?.components) {
        Object.values(currentBuilding.model.structure.components).forEach(comp => {
            if (comp.type === BaseType.Window) {
                totalWindowCount++;
                
                // Get parent surfaces
                const parentSurfaces = [
                    (comp as any).parentSurface?.value,
                    (comp as any).parentSurface1?.value,
                    (comp as any).parentSurface2?.value
                ].filter(Boolean);

                let isOutdoorWindow = false;

                parentSurfaces.forEach(parentKey => {
                    const parentComp = currentBuilding.model.structure?.components?.[parentKey];
                    if (parentComp) {
                        const parentType = parentComp.type;
                        
                        // Outdoor surfaces: Roof, Facade
                        if (parentType === BaseType.Roof || parentType === BaseType.Facade) {
                            isOutdoorWindow = true;
                            if (!outdoorWindowsByParent[parentKey]) {
                                outdoorWindowsByParent[parentKey] = {
                                    parent: new BaseComponentImpl(parentComp),
                                    windows: []
                                };
                            }
                            outdoorWindowsByParent[parentKey].windows.push(new BaseComponentImpl(comp));
                        }
                        // Indoor surfaces: Wall, Ceiling, Floor
                        else if (parentType === BaseType.Wall || parentType === BaseType.Ceiling || parentType === BaseType.Floor || parentType === BaseType.InteriorWall) {
                            // Find the room this parent belongs to
                            const roomKey = parentComp.roomKey;
                            if (roomKey && currentBuilding.model.structure?.rooms?.[roomKey]) {
                                const room = currentBuilding.model.structure.rooms[roomKey];
                                const roomImpl = new RoomImpl(room);
                                
                                // Get level
                                let level: LevelImpl | undefined;
                                if (room.levelKey && currentBuilding.model.structure?.levels?.[room.levelKey]) {
                                    level = new LevelImpl(currentBuilding.model.structure.levels[room.levelKey]);
                                }
                                
                                if (!indoorWindowsByRoom[roomKey]) {
                                    indoorWindowsByRoom[roomKey] = {
                                        room: roomImpl,
                                        level: level,
                                        windows: []
                                    };
                                }
                                
                                // Only add if not already added (might have multiple parent surfaces in same room)
                                const windowImpl = new BaseComponentImpl(comp);
                                if (!indoorWindowsByRoom[roomKey].windows.some(w => w.key === windowImpl.key)) {
                                    indoorWindowsByRoom[roomKey].windows.push(windowImpl);
                                }
                            }
                        }
                    }
                });
            }
        });
    }

    // Sort outdoor parents by type (Roof first, then Facade)
    const sortedOutdoorParentKeys = Object.keys(outdoorWindowsByParent).sort((a, b) => {
        const parentA = outdoorWindowsByParent[a].parent;
        const parentB = outdoorWindowsByParent[b].parent;
        
        // Sort by type first
        if (parentA.type === BaseType.Roof && parentB.type !== BaseType.Roof) return -1;
        if (parentA.type !== BaseType.Roof && parentB.type === BaseType.Roof) return 1;
        if (parentA.type === BaseType.Facade && parentB.type !== BaseType.Facade) return -1;
        if (parentA.type !== BaseType.Facade && parentB.type === BaseType.Facade) return 1;
        
        // Then by display name
        return parentA.displayName(currentBuilding).localeCompare(parentB.displayName(currentBuilding));
    });

    // Sort indoor rooms by level, then by room name
    const sortedRoomKeys = Object.keys(indoorWindowsByRoom).sort((a, b) => {
        const roomA = indoorWindowsByRoom[a];
        const roomB = indoorWindowsByRoom[b];
        
        // Sort by level first
        const levelNumA = roomA.level?.number ?? 0;
        const levelNumB = roomB.level?.number ?? 0;
        if (levelNumA !== levelNumB) {
            return levelNumA - levelNumB;
        }
        
        // Then by room name
        return roomA.room.displayName().localeCompare(roomB.room.displayName());
    });

    const crumbs: BreadcrumbItem[] = [
        {
            text: currentBuilding.summary.name ?? "Byggnad",
            link: `/building/${buildingKey}`
        },
        {
            text: "Fönster"
        }
    ];

    return (
        <>
            <Helmet>
                <title>{currentBuilding?.summary?.name ? `Fönster - ${currentBuilding.summary.name} - Molnfastigheter` : 'Fönster - Molnfastigheter'}</title>
            </Helmet>
            <BreadcrumbComponent items={crumbs} />
            <div style={{ padding: "20px", backgroundColor: "#f4f4f4" }}>
                <h3>Fönster ({totalWindowCount} st)</h3>

                {/* Two-pane layout */}
                <div style={{ display: 'flex', gap: '40px', width: '100%' }}>
                    {/* Left Pane - 60% width, max 900px */}
                    <div style={{ width: '60%', maxWidth: '900px', minWidth: '400px' }}>
                        <div style={{
                            width: '100%',
                            backgroundColor: 'white',
                            borderRadius: '10px',
                            padding: '40px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}>
                    {totalWindowCount === 0 ? (
                        <p>Inga fönster finns registrerade för denna byggnad.</p>
                    ) : (
                        <>
                            {/* Outdoor Windows Section */}
                            {sortedOutdoorParentKeys.length > 0 && (
                                <div style={{ marginBottom: sortedRoomKeys.length > 0 ? '50px' : '0' }}>
                                    {sortedOutdoorParentKeys.map(parentKey => {
                                        const { parent, windows } = outdoorWindowsByParent[parentKey];
                                        
                                        return (
                                            <div key={parentKey} style={{ marginBottom: '40px' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginBottom: '20px',
                                                    paddingBottom: '10px',
                                                    borderBottom: '2px solid #e0e0e0'
                                                }}>
                                                    <h5 style={{ margin: 0, marginRight: '10px', fontWeight: 500 }}>
                                                        {parent.displayName(currentBuilding)}
                                                    </h5>
                                                    <span style={{
                                                        backgroundColor: '#f0f0f0',
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        fontSize: '14px',
                                                        color: '#666'
                                                    }}>
                                                        {windows.length} {windows.length === 1 ? 'fönster' : 'fönster'}
                                                    </span>
                                                </div>

                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                                    gap: '20px'
                                                }}>
                                                    {windows.map(window => (
                                                        <ComponentCard
                                                            key={window.key}
                                                            component={window}
                                                            building={currentBuilding}
                                                            buildingKey={buildingKey}
                                                            onNavigate={() => setSelectedWindow(window)}
                                                            isSelected={selectedWindow?.key === window.key}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Indoor Windows Section */}
                            {sortedRoomKeys.length > 0 && (
                                <div>
                                    {sortedRoomKeys.map(roomKey => {
                                        const { room, level, windows } = indoorWindowsByRoom[roomKey];
                                        
                                        return (
                                            <div key={roomKey} style={{ marginBottom: '40px' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginBottom: '20px',
                                                    paddingBottom: '10px',
                                                    borderBottom: '2px solid #e0e0e0'
                                                }}>
                                                    <h5 style={{ margin: 0, marginRight: '10px', fontWeight: 500 }}>
                                                        {level ? level.displayName() : 'Okänd våning'} - {room.displayName()}
                                                    </h5>
                                                    <span style={{
                                                        backgroundColor: '#f0f0f0',
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        fontSize: '14px',
                                                        color: '#666'
                                                    }}>
                                                        {windows.length} {windows.length === 1 ? 'fönster' : 'fönster'}
                                                    </span>
                                                </div>

                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                                    gap: '20px'
                                                }}>
                                                    {windows.map(window => (
                                                        <ComponentCard
                                                            key={window.key}
                                                            component={window}
                                                            building={currentBuilding}
                                                            buildingKey={buildingKey}
                                                            onNavigate={() => setSelectedWindow(window)}
                                                            isSelected={selectedWindow?.key === window.key}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                        </div>
                    </div>

                    {/* Right Pane - Takes up remaining space, max 700px */}
                    <div style={{ flex: '1', minWidth: '300px', maxWidth: '700px' }}>
                        {selectedWindow && currentBuilding ? (
                            <ComponentDetailsView
                                component={selectedWindow}
                                building={currentBuilding}
                                buildingKey={buildingKey}
                                showHeader={true}
                                headerActions={
                                    <button
                                        onClick={() => window.open(`/building/${buildingKey}/component/${selectedWindow.key}`, '_blank')}
                                        style={{
                                            padding: '8px',
                                            backgroundColor: 'transparent',
                                            color: '#666',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '32px',
                                            height: '32px'
                                        }}
                                        title="Öppna i ny flik"
                                    >
                                        ↗
                                    </button>
                                }
                                containerStyle={{
                                    position: 'sticky',
                                    top: '20px'
                                }}
                            />
                        ) : (
                            <div
                                style={{
                                    width: '100%',
                                    minHeight: '400px',
                                    border: '2px dashed #ccc',
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#fafafa',
                                    position: 'sticky',
                                    top: '20px'
                                }}
                            >
                                <p
                                    style={{
                                        color: '#666',
                                        fontSize: '16px',
                                        textAlign: 'center',
                                        margin: 0,
                                        padding: '20px'
                                    }}
                                >
                                    Klicka på ett fönster för att se detaljer
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default WindowsListPage;

